import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate for redirection
import './ManageManager.css';
import api from '../services/api';

// --- NEW: AUTHENTICATED API SERVICE ---
const API_BASE_URL = api.defaults.baseURL + '/admin'

/**
 * A helper function to get the token from localStorage.
 * @returns {string|null} The auth token or null if not found.
 */
const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

/**
 * A centralized fetch wrapper that automatically adds the Authorization header.
 * @param {string} url The URL to fetch.
 * @param {object} options The options for the fetch request (e.g., method, body).
 * @returns {Promise<any>} The JSON response from the server.
 */
const fetchWithAuth = async (url, options = {}) => {
  const token = getAuthToken();
  if (!token) {
    // If there's no token, we can't make an authenticated request.
    // This will be caught by the component to show an error.
    throw new Error('Authentication token not found. Please log in again.');
  }

  // Prepare the headers, including the existing ones and the new Authorization header.
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
    'Authorization': `Bearer ${token}`, // This is the critical line
  };

  const response = await fetch(url, { ...options, headers });

  // Handle unauthorized or other critical errors
  if (response.status === 401 || response.status === 403) {
    // This means the token is invalid or expired.
    // We'll log the user out in the component that catches this error.
    throw new Error('Your session has expired. Please log in again.');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})); // Try to parse error, otherwise empty object
    throw new Error(errorData.message || `An API error occurred. Status: ${response.status}`);
  }
  
  // For DELETE requests or others that might not return a body
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return null; // Return null for no-content responses
  }

  return response.json();
};

const apiService = {
  getManagers: () => fetchWithAuth(`${API_BASE_URL}/users`),
  addManager: (payload) => fetchWithAuth(`${API_BASE_URL}/users/manager`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
  updateManager: (managerData) => fetchWithAuth(`${API_BASE_URL}/users/${managerData.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      name: managerData.name,
      email: managerData.email,
      phone: managerData.phone,
      status: managerData.status,
    }),
  }),
  deleteManager: (managerId) => fetchWithAuth(`${API_BASE_URL}/users/${managerId}`, {
    method: 'DELETE',
  }),
};

// --- Manager Modal Component (Unchanged) ---
const ManagerModal = ({ isOpen, onClose, onSave, manager, isSubmitting }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (manager) {
      setFormData(manager);
    } else {
      setFormData({ name: '', email: '', phone: '', status: 'Active', password: '', confirmPassword: '' });
    }
  }, [manager]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <header className="modal-header">
          <h2>{manager ? 'Edit Manager' : 'Add New Manager'}</h2>
        </header>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group"><label>Full Name</label><input type="text" name="name" value={formData.name || ''} onChange={handleChange} required /></div>
            <div className="form-group"><label>Email Address</label><input type="email" name="email" value={formData.email || ''} onChange={handleChange} required /></div>
            <div className="form-group"><label>Phone Number</label><input type="tel" name="phone" value={formData.phone || ''} onChange={handleChange} /></div>
            {!manager && (
              <><div className="form-group"><label>Password</label><input type="password" name="password" value={formData.password || ''} onChange={handleChange} required minLength="6" /></div><div className="form-group"><label>Confirm Password</label><input type="password" name="confirmPassword" value={formData.confirmPassword || ''} onChange={handleChange} required /></div></>
            )}
            <div className="form-group"><label>Status</label><select name="status" value={formData.status || 'Active'} onChange={handleChange}><option value="Active">Active</option><option value="Inactive">Inactive</option></select></div>
          </div>
          <footer className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="btn-save" disabled={isSubmitting}>{isSubmitting ? 'Saving...' : 'Save Manager'}</button>
          </footer>
        </form>
      </div>
    </div>
  );
};


// --- Skeleton Loader Component (Unchanged) ---
const SkeletonLoader = () => (
  <table className="manager-table">
    <thead>
      <tr><th>Name</th><th>Contact Info</th><th>Status</th><th>Actions</th></tr>
    </thead>
    <tbody>
      {[...Array(4)].map((_, i) => (
        <tr key={i}>
          <td><div className="skeleton-line" style={{ width: '60%' }}></div></td>
          <td>
            <div className="skeleton-line" style={{ width: '80%' }}></div>
            <div className="skeleton-line" style={{ width: '40%', marginTop: '0.4rem' }}></div>
          </td>
          <td><div className="skeleton-line" style={{ width: '70px' }}></div></td>
          <td><div className="skeleton-line" style={{ width: '50px' }}></div></td>
        </tr>
      ))}
    </tbody>
  </table>
);


// --- Main Component (Now using Authenticated API Service) ---
const ManageManager = () => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentManager, setCurrentManager] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate(); // Hook for programmatic navigation

  // --- UPDATED to handle authentication errors ---
  const handleApiError = useCallback((err) => {
    setError(err.message);
    // If the error indicates a session expiration, log the user out and redirect.
    if (err.message.includes('token not found') || err.message.includes('session has expired')) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      // Redirect to login page after a short delay so the user can see the message
      setTimeout(() => navigate('/login'), 2500);
    }
  }, [navigate]);

  const loadManagers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const allUsers = await apiService.getManagers();
      // Filter for managers and map data structure
      const managerUsers = allUsers
        .filter(user => user.role === 'manager')
        .map(user => ({
          id: user._id,
          name: user.name,
          email: user.email,
          phone: user.phone || 'N/A',
          status: user.status || 'Active'
        }));
      setManagers(managerUsers);
    } catch (err) { handleApiError(err); }
    finally { setLoading(false); }
  }, [handleApiError]);

  useEffect(() => { loadManagers(); }, [loadManagers]);

  const handleOpenModal = (manager = null) => {
    setCurrentManager(manager);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentManager(null);
  };

  const handleSaveManager = async (managerData) => {
    setIsSubmitting(true);
    setError('');
    try {
      if (managerData.id) {
        await apiService.updateManager(managerData);
      } else {
        if (managerData.password !== managerData.confirmPassword) {
            throw new Error("Passwords do not match.");
        }
        const payload = { name: managerData.name, email: managerData.email, password: managerData.password, role: 'manager' };
        await apiService.addManager(payload);
      }
      handleCloseModal();
      await loadManagers(); // Refresh list after successful save
    } catch (err) { handleApiError(err); }
    finally { setIsSubmitting(false); }
  };
  
  const handleDeleteManager = async (managerId) => {
    if (window.confirm('Are you sure you want to delete this manager?')) {
      const originalManagers = [...managers];
      setManagers(managers.filter(m => m.id !== managerId));
      try { 
        await apiService.deleteManager(managerId); 
      } 
      catch (err) { 
        setManagers(originalManagers);
        handleApiError(err); // Use the centralized error handler
      }
    }
  };

  return (
    <div className="manage-manager-container">
      <header className="page-header">
        <h1>Manage Managers</h1>
        <button className="btn-add" onClick={() => handleOpenModal()}>+ Add New Manager</button>
      </header>
      {error && <div className="error-banner" onClick={() => setError('')}>{error} ×</div>}
      <div className="manager-table-card">
        {loading ? <SkeletonLoader /> : (
          <table className="manager-table">
            <thead><tr><th>Name</th><th>Contact Info</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {managers.length > 0 ? managers.map(manager => (
                <tr key={manager.id}>
                  <td>{manager.name}</td>
                  <td>{manager.email}<br/><span className="phone">{manager.phone}</span></td>
                  <td><span className={`status-badge status-${(manager.status || 'active').toLowerCase()}`}>{manager.status || 'Active'}</span></td>
                  <td className="actions-cell">
                    <button className="btn-edit" onClick={() => handleOpenModal(manager)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDeleteManager(manager.id)}>Delete</button>
                  </td>
                </tr>
              )) : (<tr><td colSpan="4" style={{ textAlign: 'center' }}>No managers found.</td></tr>)}
            </tbody>
          </table>
        )}
      </div>
      <ManagerModal isOpen={isModalOpen} onClose={handleCloseModal} onSave={handleSaveManager} manager={currentManager} isSubmitting={isSubmitting}/>
    </div>
  );
};

export default ManageManager; 